/**
 * Mathe-Modus: reduziertes UI (4 Operationen × 3 Levels).
 * Save/Load via API mit Auto-Save.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Save, Printer, RotateCcw, Volume2, Check, Trash2 } from 'lucide-react';
import {
  MATH_OPS,
  MATH_LEVELS,
  type MathOp,
  type MathLevel,
  getExercise,
  calcExercise,
} from '@schreibhilfe/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSpeak } from '@/lib/audio';
import { useValidActiveProfile } from '@/lib/use-valid-profile';
import { readDraft, useDocAutosave } from '@/lib/doc-autosave';
import { printWithTip } from '@/lib/print';
import { documentsApi } from '@/lib/api';
import { useSettings } from '@/lib/settings';
import { AuthGuard } from '@/lib/auth-guard';
import { PrintHeader } from '@/components/print-header';

export const Route = createFileRoute('/rechnen')({
  validateSearch: (s) => ({ doc: (s.doc as string | undefined) ?? undefined }),
  component: () => (
    <AuthGuard>
      <MathScreen />
    </AuthGuard>
  ),
});

function MathScreen() {
  const speak = useSpeak();
  const settings = useSettings();
  const { activeProfileId } = useValidActiveProfile();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const [text, setText] = useState('');
  const [op, setOp] = useState<MathOp>('plus');
  const [level, setLevel] = useState<MathLevel>('leicht');
  const [docId, setDocId] = useState<string | null>(search.doc ?? null);

  const exercise = getExercise(op, level);

  const { data: loadedDoc } = useQuery({
    queryKey: ['document', docId],
    queryFn: () => documentsApi.get(docId!),
    enabled: !!docId,
  });

  const saver = useDocAutosave({
    type: 'math',
    profileId: activeProfileId,
    text,
    docId,
    onDocId: setDocId,
    loadedContent: loadedDoc?.content ?? null,
    autoSave: settings.autoSave,
    minLength: 3,
  });

  useEffect(() => {
    if (!loadedDoc) return;
    // Gerade selbst angelegt → Editor nicht mit dem Server-Stand überschreiben
    // (sonst gingen Buchstaben verloren, die während des Speicherns getippt wurden).
    if (saver.isOwnDoc(loadedDoc.id)) return;
    const draft = readDraft('math', activeProfileId);
    const useDraft =
      draft?.docId === loadedDoc.id &&
      draft.at > new Date(loadedDoc.updatedAt).getTime() &&
      draft.content !== loadedDoc.content;
    setText(useDraft ? draft!.content : loadedDoc.content);
    saver.setLastSaved(new Date(loadedDoc.updatedAt));
    if (useDraft) toast.info('Nicht gespeicherte Änderungen wiederhergestellt.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedDoc]);

  useEffect(() => {
    if (search.doc) return;
    const draft = readDraft('math', activeProfileId);
    if (!draft) return;
    setText(draft.content);
    if (draft.docId) setDocId(draft.docId);
    toast.info('Nicht gespeicherte Rechnungen wiederhergestellt.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileId]);

  // === Aufgaben generieren ===
  function appendOne() {
    if (!exercise) return;
    const sep = text && !text.endsWith('\n') ? '\n' : '';
    setText(text + sep + exercise.generate());
  }
  function appendFive() {
    if (!exercise) return;
    const lines = Array.from({ length: 5 }, () => exercise.generate()).join('\n');
    const sep = text && !text.endsWith('\n') ? '\n' : '';
    setText(text + sep + lines + '\n');
  }

  // === Lösung berechnen ===
  function calcLast() {
    const lines = text.split('\n');
    let idx = lines.length - 1;
    while (idx >= 0 && !lines[idx]!.trim()) idx--;
    if (idx < 0) return;
    const result = calcExercise(lines[idx]!);
    if (result === null) return;
    lines[idx] = lines[idx]!.replace(/=\s*\S*\s*$/, '') + '= ' + result;
    setText(lines.join('\n'));
    speak(`gleich ${result}`, { short: true });
  }

  function speakAll() {
    if (!text.trim()) return;
    let t = text
      .replace(/\+/g, ' plus ')
      .replace(/×/g, ' mal ')
      .replace(/÷/g, ' geteilt durch ')
      .replace(/[-−]/g, ' minus ')
      .replace(/=/g, ' gleich ')
      .replace(/\s+/g, ' ');
    speak(t);
  }

  if (!activeProfileId) {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-bold text-gray-700">Kein Profil ausgewählt</p>
          <p className="mt-2 text-sm text-gray-500">
            Wähle im Lehrer-Bereich ein aktives Schüler:innen-Profil aus.
          </p>
          <Button asChild className="mt-4">
            <Link to="/lehrer">Zum Lehrer-Bereich</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-sky-bottom">
      <header className="flex h-16 items-center gap-2 border-b-2 border-danger/20 bg-white px-3">
        <Button asChild variant="ghost" size="icon" aria-label="Zurück">
          <Link to="/">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-danger font-bold text-white">
          123
        </div>
        <div className="flex-1">
          <h1 className="text-base font-bold text-danger">Rechnen</h1>
          <p className="text-xs text-gray-500">
            {saver.isPending
              ? 'speichere…'
              : saver.lastSaved
                ? `gespeichert ${saver.lastSaved.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}`
                : 'Wähle Operation & Schwierigkeit'}
          </p>
        </div>
        <Button variant="success" size="sm" onClick={calcLast}>
          <RotateCcw className="h-4 w-4" /> Lösung
        </Button>
        <Button variant="success" size="sm" onClick={speakAll}>
          <Volume2 className="h-4 w-4" /> Vorlesen
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void saver.saveNow()}
          disabled={saver.isPending || !text.trim()}
        >
          {saver.justSaved && !saver.isPending ? (
            <Check className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}{' '}
          Speichern
        </Button>
        <Button variant="primary" size="sm" onClick={printWithTip}>
          <Printer className="h-4 w-4" /> Drucken
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={async () => {
            const lose = saver.isDirty && !settings.autoSave;
            const ok = confirm(
              lose
                ? 'Neu beginnen? Die aktuellen Rechnungen sind noch NICHT gespeichert und gehen verloren.'
                : 'Neu beginnen? Die bisherigen Rechnungen bleiben gespeichert.',
            );
            if (ok) {
              if (saver.isDirty && settings.autoSave) {
                // Nur weitermachen, wenn das Speichern wirklich geklappt hat —
                // sonst bliebe der Text weder am Server noch auf dem Gerät.
                const saved = await saver.saveNow();
                if (!saved) return;
              }
              saver.reset();
              setText('');
              setDocId(null);
              saver.setLastSaved(null);
              navigate({ to: '/rechnen', search: { doc: undefined } }).catch(() => {});
            }
          }}
        >
          <Trash2 className="h-4 w-4" /> Neu
        </Button>
      </header>

      <main className="flex flex-1 flex-col gap-3 p-4">
        <PrintHeader section="Rechnen" />
        {/* Reduziertes UI: 4 Ops + 3 Levels + Generate */}
        <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-white p-3 shadow-sm">
          <span className="text-sm font-bold text-gray-500">Übung:</span>
          <div className="flex flex-wrap gap-1.5">
            {MATH_OPS.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setOp(o.id)}
                className={cn(
                  'rounded-full border-2 px-4 py-1.5 text-sm font-bold transition-all',
                  op === o.id
                    ? 'border-danger bg-danger text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-danger',
                )}
              >
                <span className="mr-1 opacity-75">{o.symbol}</span>
                {o.label}
              </button>
            ))}
          </div>
          <div className="h-6 w-px bg-gray-300" />
          <div className="flex flex-wrap gap-1.5">
            {MATH_LEVELS.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => setLevel(l.id)}
                className={cn(
                  'rounded-full border-2 px-3.5 py-1.5 text-sm font-bold transition-all',
                  level === l.id
                    ? 'border-primary bg-primary text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-primary',
                )}
              >
                {l.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="success" size="sm" onClick={appendOne}>
              + 1 Aufgabe
            </Button>
            <Button variant="success" size="sm" onClick={appendFive}>
              + 5 Aufgaben
            </Button>
          </div>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Klick oben auf '+ 1 Aufgabe' — oder tippe direkt: z.B. 3 + 4 = 7"
          autoComplete="off"
          spellCheck={false}
          className="flex-1 resize-none rounded-2xl border-4 border-danger/20 bg-white p-6 font-mono text-3xl font-semibold leading-relaxed outline-none focus:border-danger"
          style={{ letterSpacing: '1px' }}
        />
      </main>
    </div>
  );
}
